import React, { useRef, useState } from "react";
import '../assets/styles/app.css';
import { personalInformation } from "../messages/personal-Information";
import { common } from "../messages/common-items";
import { DefaultButton, Dialog, DialogFooter, DialogType, IconButton, PrimaryButton, Stack } from "@fluentui/react";

interface FileUploadProps {
  label: string;
  placeholder: string;
  multiple?: boolean;
  accept?: string;
  onFileSelect?: (files: File[]) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
  label,
  placeholder,
  multiple = true,
  accept = "image/*",
  onFileSelect,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [hideDialog, setHideDialog] = useState(true);
  const [fileToDeleteIndex, setFileToDeleteIndex] = useState<number | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const { Labels: Lbl_Personal_Info} = personalInformation;
  const { Labels: Lbl_Common, SuccessMessages: Success_Common } = common;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      const updatedFiles = multiple ? [...files, ...selectedFiles] : selectedFiles;
      setFiles(updatedFiles);
      if (onFileSelect) onFileSelect(updatedFiles);
      e.target.value = "";
    }
  };

  const openDialog = (index: number) => {
    setFileToDeleteIndex(index);
    setHideDialog(false);
  };

  const closeDialog = () => {
    setFileToDeleteIndex(null);
    setHideDialog(true);
  };

  const onConfirmDelete = () => {
    if (fileToDeleteIndex !== null) {
      const updatedFiles = files.filter((_, i) => i !== fileToDeleteIndex);
      setFiles(updatedFiles);
      if (onFileSelect) onFileSelect(updatedFiles);
    }
    closeDialog();
  };

  return (
    <div className="fileUploadRoot">
      <label className="fileUploadLabel">{label}</label>
      <div className="fileUploadContainer">
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
          multiple={multiple}
          accept={accept}
        />
        <DefaultButton
          text={Lbl_Personal_Info.UPLOAD_BTN}
          onClick={() => fileInputRef.current?.click()}
          className="customUploadButton"
        />
        <span className="fileUploadText" style={{ color: "#605e5c" }}>
          {files.length > 0
            ? `${files[0].name}`
            : placeholder}
        </span>
      </div>

      {/* Thumbnails */}
      {files.length > 0 && (
        <Stack horizontal wrap tokens={{ childrenGap: 10 }} style={{ marginTop: 8 }}>
          {files.map((file, i) => {
            const fileUrl = URL.createObjectURL(file);
            return (
              <div key={i} className="imagePreviewItem" style={{ position: "relative" }}>
                <img
                  src={fileUrl}
                  alt={file.name}
                  className="imagePreview"
                  style={{ cursor: "pointer" }}
                  onClick={() => setPreviewImage(fileUrl)}
                />
                <IconButton
                  iconProps={{ iconName: "Cancel" }}
                  className="imagePreviewClose"
                  onClick={() => openDialog(i)}
                />
              </div>
            );
          })}
        </Stack>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        hidden={hideDialog}
        onDismiss={closeDialog}
        dialogContentProps={{
          type: DialogType.normal,
          title: Lbl_Common.DELETE,
          subText: Success_Common.DELETE_CONFIRMATION_MSG,
        }}
        modalProps={{ isBlocking: true }}
      >
        <DialogFooter>
          <PrimaryButton
            onClick={onConfirmDelete}
            text={Lbl_Common.DELETE}
            styles={{
              root: { backgroundColor: "#d13438", border: "none" },
            }}
          />
          <DefaultButton onClick={closeDialog} text={Lbl_Common.CANCEL} />
        </DialogFooter>
      </Dialog>

      {/* Image Preview Dialog */}
      {previewImage && (
        <Dialog
          hidden={!previewImage}
          onDismiss={() => setPreviewImage(null)}
          dialogContentProps={{
            type: DialogType.normal
          }}
          modalProps={{ isBlocking: false }}
        >
          <IconButton
            iconProps={{ iconName: "Cancel" }}
            className="imagePreviewClose imagePreviewDialogClose"
            onClick={() => setPreviewImage(null)}
          />
          <img
            src={previewImage}
            alt="Preview"
            className="imagePreviewDialog"
          />
        </Dialog>
      )}
    </div>
  );
};

export default FileUpload;